import {Injectable} from '@angular/core';
import { HttpClient, HttpEventType, HttpRequest, HttpResponse } from '@angular/common/http';
import {Observable, Subject} from 'rxjs';
import { environment } from 'src/environments/environment';
import * as LogRocket from 'logrocket';

@Injectable()
export class UploadService {
  // ENG: upload() terminates its progress streams on failure so callers never hang.
  // Because the progress observables complete (rather than error) on failure -- which
  // keeps the 6 existing callers that pass no error callback working unchanged --
  // the actual failure is surfaced here for callers that want to react to it.
  private uploadErrorSubject = new Subject<any>();
  public uploadError$ = this.uploadErrorSubject.asObservable();

  /** Monotonic id so every upload() call's events can be correlated in a replay. */
  private uploadSeq = 0;

  constructor(private http: HttpClient) { }

  /**
   * ENG-5428 diagnostics. We are not certain the hang merchants reported (SMS
   * campaign stuck on the loading bar on iPad/iPhone) was only the missing error
   * handler, so every upload transition is breadcrumbed onto the LogRocket
   * timeline. If it recurs, the session replay shows exactly how far the submit
   * got: whether the request was even issued, the last upload progress reached,
   * whether it failed/aborted, and with what status.
   *
   * Kept intentionally cheap and never able to break an upload: guarded on the
   * environment flag and swallowing its own errors.
   */
  private lrTrack(event: string, props: { [key: string]: string | number | boolean }): void {
    if (!environment.logRocket?.enabled) { return; }
    try {
      if (typeof LogRocket.track === 'function') {
        LogRocket.track(event, props);
      }
    } catch (e) { /* diagnostics must never affect the upload */ }
  }

  private lrCapture(message: string, props: { [key: string]: string | number | boolean }): void {
    if (!environment.logRocket?.enabled) { return; }
    try {
      if (typeof LogRocket.captureMessage === 'function') {
        LogRocket.captureMessage(message, { tags: { feature: 'upload' }, extra: props });
      }
    } catch (e) { /* diagnostics must never affect the upload */ }
  }

  /** Flatten an HttpErrorResponse (or anything else) into LogRocket-safe scalars. */
  private describeError(err: any): { [key: string]: string | number | boolean } {
    return {
      status: err?.status ?? -1,
      statusText: String(err?.statusText ?? ''),
      errorName: String(err?.name ?? ''),
      errorMessage: String(err?.message ?? err ?? ''),
      // 0 with an offline navigator is the signature of the iOS abort/connection
      // drop we suspect; recording it explicitly makes the replay unambiguous.
      online: typeof navigator !== 'undefined' ? navigator.onLine : true,
    };
  }

  /**
   * Keep the upload out of the Angular service worker.
   *
   * Safari and iOS 26.5+ (WebKit bug 319985) send an XHR/fetch whose body holds
   * a disk-backed File -- a photo from the picker -- with Content-Length 0 when
   * the page is controlled by a service worker that re-issues the request. The
   * ngsw worker does exactly that for every request, POSTs included, so the
   * campaign form reached the backend with no fields at all and it answered 500
   * (CANA, iPhone, 2026-10-10). The worker leaves a request alone when the URL
   * carries `ngsw-bypass`. The query form is used rather than the header because
   * the backend's CORS allow-list does not include an `ngsw-bypass` header and a
   * preflight would reject it.
   */
  private bypassServiceWorker(url: string): string {
    return url + (url.includes('?') ? '&' : '?') + 'ngsw-bypass=true';
  }

  /**
   * Copy a picked File into memory before it goes into the FormData.
   *
   * Same WebKit bug: a few reports see the empty body even with the service
   * worker out of the way, and the common thread is the File being backed by a
   * temporary file on disk. An in-memory Blob has no such backing. If the read
   * fails or comes back short, the original File is sent so nothing is worse
   * than before.
   */
  private async materialize(file: File): Promise<Blob> {
    try {
      const buffer = await file.arrayBuffer();
      if (file.size > 0 && buffer.byteLength !== file.size) {
        this.lrTrack('Upload File Read Short', { fileName: file.name, fileBytes: file.size, readBytes: buffer.byteLength });
        return file;
      }
      return new Blob([buffer], { type: file.type });
    } catch (e) {
      this.lrTrack('Upload File Read Failed', { fileName: file.name, fileBytes: file.size, errorMessage: String(e) });
      return file;
    }
  }

  getvoiceAttendantRecord() {
    return this.http.get<any>(`${environment.apiUrl}/backend/voice/list/`);
  }
  actionChangeVoice(data: any) {
    return this.http.put<any>(`${environment.apiUrl}/backend/v2/ivr_setting/ivr_change/voice/`, data);
  }

  favUpload(data:any){
    return this.http.post<any>(`${environment.apiUrl}/backend/v2/stores/favicon/`, data);
  }

  logoUpload(data:any){
    return this.http.post<any>(`${environment.apiUrl}/backend/v2/stores/logo/`, data);
  }


  upload(url: string, files: Set<File>, data: Object): { [key: string]: { progress: Observable<number> } } {

    // this will be the our resulting map
    const status: { [key: string]: { progress: Observable<number> } } = {};

    // ENG-5428: correlate every event below to one upload() call.
    const uploadId = ++this.uploadSeq;
    const startedAt = Date.now();
    let totalBytes = 0;
    files.forEach(f => totalBytes += (f?.size || 0));
    this.lrTrack('Upload Started', {
      uploadId,
      url,
      fileCount: files.size,
      totalBytes,
      // Photos straight off an iPhone/iPad camera are the large payloads we
      // suspect; recording the size lets us correlate failures with it.
      largestFileMB: +(totalBytes / (1024 * 1024)).toFixed(2),
    });

    if (files.size <= 0) {
      const postFormData: FormData = new FormData();
      for (var key in data) {
        postFormData.append(key, data[key])
      }
      const req = new HttpRequest('POST', this.bypassServiceWorker(url), postFormData);
      let progress = new Subject<number>();
      // this.http.request(req).subscribe(e => {
      //   progress.next(100);
      //   progress.complete();
      // })
      // ENG: the old subscribe had no error handler, so a failed/aborted request
      // left this Subject open forever. Callers use forkJoin on these observables,
      // which only fires on complete() -- so the caller's dialog spun indefinitely
      // with no error shown. Always terminate the stream, and report the failure
      // separately via uploadError$ so existing callers keep their exact behaviour.
      this.http.request(req).subscribe(
        e => {
          progress.next(100);
          progress.complete();
          // ENG-5428: only fires for the final HttpResponse -- the request went through.
          if (e instanceof HttpResponse) {
            this.lrTrack('Upload Succeeded', {
              uploadId,
              url,
              kind: 'form',
              httpStatus: e.status,
              elapsedMs: Date.now() - startedAt,
            });
          }
        },
        err => {
          this.uploadErrorSubject.next(err);
          progress.complete();
          // ENG-5428: this is the branch that used to be missing entirely and left
          // the caller's loading bar spinning forever.
          const detail = this.describeError(err);
          this.lrTrack('Upload Failed', {
            ...detail,
            uploadId,
            url,
            kind: 'form',
            elapsedMs: Date.now() - startedAt,
          });
          this.lrCapture(`Upload failed (form) status=${detail.status}`, {
            ...detail,
            uploadId,
            url,
          });
        }
      )
      status['form'] = { progress: progress.asObservable() }
    } else {
      files.forEach(file => {
        // create a new multipart-form for every file
        const formData: FormData = new FormData();
        for (var key in data) {
          formData.append(key, data[key])
        }
        // create a new progress-subject for every file
        const progress = new Subject<number>();

        // The File is copied into memory and the request is sent once that
        // resolves; the progress Subject is already in the returned map, so
        // callers see no difference.
        this.materialize(file).then(body => {
        formData.append('document', body, file.name);

        // create a http-post request and pass the form
        // tell it to report the upload progress
        const req = new HttpRequest('POST', this.bypassServiceWorker(url), formData, {
          reportProgress: true
        });

        // send the http-request and subscribe for progress-updates
        // this.http.request(req).subscribe(event => {
        //   if (event.type === HttpEventType.UploadProgress) {
        //
        //     // calculate the progress percentage
        //     const percentDone = Math.round(100 * event.loaded / event.total);
        //
        //     // pass the percentage into the progress-stream
        //     progress.next(percentDone);
        //   } else if (event instanceof HttpResponse) {
        //
        //     // Close the progress-stream if we get an answer form the API
        //     // The upload is complete
        //     progress.complete();
        //   }
        // });
        // ENG: progress.complete() was only reachable on HttpResponse. An upload that
        // failed or was aborted mid-flight (common on iOS Safari, which cancels
        // in-flight requests when the web view is backgrounded by the photo picker)
        // never completed the Subject, so the caller's forkJoin never emitted and the
        // UI was left on an indeterminate loading bar with no error.
        // ENG-5428: track how far the body actually got before any failure. If a
        // merchant hits the hang again, the replay shows the last percentage
        // reached -- an upload that dies at e.g. 60% points at the connection
        // being dropped mid-flight (iOS backgrounding the web view / weak
        // network) rather than the server rejecting the request.
        let lastPercent = 0;
        let sentBytes = 0;
        let sawResponse = false;
        this.http.request(req).subscribe(
          event => {
            if (event.type === HttpEventType.UploadProgress) {

              // calculate the progress percentage
              const percentDone = event.total
                ? Math.round(100 * event.loaded / event.total)
                : 0;

              lastPercent = percentDone;
              sentBytes = event.loaded || 0;

              // pass the percentage into the progress-stream
              progress.next(percentDone);
            } else if (event instanceof HttpResponse) {

              sawResponse = true;
              // Close the progress-stream if we get an answer form the API
              // The upload is complete
              progress.complete();
              this.lrTrack('Upload Succeeded', {
                uploadId,
                url,
                kind: 'file',
                fileName: file.name,
                fileBytes: file.size || 0,
                httpStatus: event.status,
                elapsedMs: Date.now() - startedAt,
              });
            }
          },
          err => {
            // Terminate the stream so callers stop waiting, and report the failure.
            this.uploadErrorSubject.next(err);
            progress.complete();
            const detail = this.describeError(err);
            this.lrTrack('Upload Failed', {
              ...detail,
              uploadId,
              url,
              kind: 'file',
              fileName: file.name,
              fileBytes: file.size || 0,
              // The two fields that separate "server said no" from "connection
              // died part-way through the body".
              lastPercent,
              sentBytes,
              sawResponse,
              elapsedMs: Date.now() - startedAt,
            });
            this.lrCapture(
              `Upload failed (file) status=${detail.status} at ${lastPercent}%`,
              { ...detail, uploadId, url, fileName: file.name, lastPercent, sentBytes },
            );
          }
        );
        });

        // Save every progress-observable in a map of all observables
        status[file.name] = {
          progress: progress.asObservable()
        };
      });
    }
    // return the map of progress.observables
    return status;
  }
}
